import React, { useState, useRef, useEffect } from 'react';

export const GalleryVideoItem = ({ src, poster, alt, className, style, loop = true, onClickContainer }) => {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [thumbnailUrl, setThumbnailUrl] = useState(poster || null);

    useEffect(() => {
        if (poster) {
            setThumbnailUrl(poster);
            return;
        }

        if (!src) return;

        let isMounted = true;
        const tempVideo = document.createElement('video');
        tempVideo.crossOrigin = 'anonymous';
        tempVideo.src = src;
        tempVideo.muted = true;
        tempVideo.playsInline = true;
        tempVideo.preload = 'auto';

        const captureFrame = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = tempVideo.videoWidth || 640;
                canvas.height = tempVideo.videoHeight || 360;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(tempVideo, 0, 0, canvas.width, canvas.height);
                const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
                if (isMounted && dataUrl && dataUrl.length > 100) {
                    setThumbnailUrl(dataUrl);
                }
            } catch (err) {
                console.warn("Could not capture video frame for thumbnail:", err);
            }
        };

        const handleSeeked = () => {
            captureFrame();
            tempVideo.removeEventListener('seeked', handleSeeked);
        };

        const handleLoadedData = () => {
            if (tempVideo.duration > 0.5) {
                tempVideo.currentTime = 0.5;
            } else if (tempVideo.duration > 0) {
                tempVideo.currentTime = tempVideo.duration / 2;
            } else {
                captureFrame();
            }
        };

        tempVideo.addEventListener('loadeddata', handleLoadedData);
        tempVideo.addEventListener('seeked', handleSeeked);

        return () => {
            isMounted = false;
            tempVideo.removeEventListener('loadeddata', handleLoadedData);
            tempVideo.removeEventListener('seeked', handleSeeked);
            tempVideo.src = '';
            tempVideo.remove();
        };
    }, [src, poster]);

    const togglePlay = (e) => {
        if (e) e.stopPropagation();
        if (!videoRef.current) return;
        if (isPlaying) {
            videoRef.current.pause();
        } else {
            setIsPlaying(true);
            videoRef.current.play().catch(err => {
                console.error("Video play error:", err);
                setIsPlaying(false);
            });
        }
    };

    return (
        <div 
            className={className}
            style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                backgroundColor: '#0f172a',
                overflow: 'hidden',
                ...style
            }}
            onClick={onClickContainer}
        >
            <video
                ref={videoRef}
                src={src}
                poster={thumbnailUrl || poster}
                preload="metadata"
                playsInline
                loop={Boolean(loop)}
                controls={isPlaying}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => { if (!loop) setIsPlaying(false); }}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: style?.objectFit || 'cover',
                    display: 'block'
                }}
            />

            {!isPlaying && thumbnailUrl && (
                <img
                    src={thumbnailUrl}
                    alt={alt || "Video preview"}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: style?.objectFit || 'cover',
                        zIndex: 5,
                        pointerEvents: 'none'
                    }}
                />
            )}

            {!isPlaying && (
                <button
                    type="button"
                    onClick={togglePlay}
                    aria-label="Play video"
                    title="Click to play video"
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        background: 'rgba(15, 23, 42, 0.85)',
                        backdropFilter: 'blur(8px)',
                        border: '2px solid rgba(255, 255, 255, 0.9)',
                        color: '#ffffff',
                        fontSize: '22px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        zIndex: 10,
                        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.5)',
                        transition: 'transform 0.2s, background 0.2s',
                        paddingLeft: '4px'
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.1)';
                        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.95)';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
                        e.currentTarget.style.background = 'rgba(15, 23, 42, 0.85)';
                    }}
                >
                    ▶
                </button>
            )}
        </div>
    );
};

export default GalleryVideoItem;
